/**
 * ChartExportPage - Server-side chart rendering for report exports
 * 
 * This page renders individual charts in isolation for screenshot capture.
 * URL format: /chart-export?chart={chartId}&tenant={tenantId}&start={date}&end={date}&token={sessionToken}
 * 
 * Charts render full-width with transparent/white background for clean PNG export.
 */

import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, ComposedChart
} from "recharts";

// Chart color palette
const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#84CC16"];
const PRIMARY_COLOR = "#3B82F6";
const SECONDARY_COLOR = "#10B981";

const ChartExportPage = () => {
  const [searchParams] = useSearchParams();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const chartId = searchParams.get("chart");
  const tenantId = searchParams.get("tenant");
  const startDate = searchParams.get("start");
  const endDate = searchParams.get("end");
  const token = searchParams.get("token");
  const width = parseInt(searchParams.get("width") || "800");
  const height = parseInt(searchParams.get("height") || "400");

  useEffect(() => {
    const fetchChartData = async () => {
      if (!chartId || !tenantId || !token) {
        setError("Missing required parameters");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/reports/chart-data/${chartId}`, {
          params: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-ID": tenantId,
            start_date: startDate,
            end_date: endDate
          }
        });
        setChartData(response.data);
      } catch (err) {
        console.error("Chart data fetch error:", err);
        setError(err.response?.data?.detail || "Failed to load chart data");
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [chartId, tenantId, startDate, endDate, token]);

  if (loading) {
    return (
      <div id="chart-loading" style={{ width, height, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff" }}>
        <p>Loading chart...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div id="chart-error" style={{ width, height, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", color: "#EF4444" }}>
        <p>{error}</p>
      </div>
    );
  }

  // Render the appropriate chart based on chartId
  const renderChart = () => {
    if (!chartData) return null;

    const { chart_type, data, title, config = {} } = chartData;

    switch (chart_type) {
      case "line":
        return (
          <ResponsiveContainer width="100%" height={height - 60}>
            <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey={config.xKey || "date"} tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              {(config.lines || ["value"]).map((line, idx) => (
                <Line
                  key={line}
                  type="monotone"
                  dataKey={line}
                  stroke={COLORS[idx % COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  name={config.lineNames?.[line] || line}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case "bar":
        return (
          <ResponsiveContainer width="100%" height={height - 60}>
            <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey={config.xKey || "name"} tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              {(config.bars || ["value"]).map((bar, idx) => (
                <Bar
                  key={bar}
                  dataKey={bar}
                  fill={COLORS[idx % COLORS.length]}
                  name={config.barNames?.[bar] || bar}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case "area":
        return (
          <ResponsiveContainer width="100%" height={height - 60}>
            <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey={config.xKey || "date"} tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              {(config.areas || ["value"]).map((area, idx) => (
                <Area
                  key={area}
                  type="monotone"
                  dataKey={area}
                  stroke={COLORS[idx % COLORS.length]}
                  fill={COLORS[idx % COLORS.length]}
                  fillOpacity={0.3}
                  name={config.areaNames?.[area] || area}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );

      case "pie":
        return (
          <ResponsiveContainer width="100%" height={height - 60}>
            <PieChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={Math.min(width, height) / 3}
                dataKey={config.valueKey || "value"}
                nameKey={config.nameKey || "name"}
              >
                {data.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      case "funnel":
        // Horizontal bar chart styled as funnel
        return (
          <ResponsiveContainer width="100%" height={height - 60}>
            <BarChart data={data} layout="vertical" margin={{ top: 20, right: 30, left: 100, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={90} />
              <Tooltip />
              <Bar dataKey="value" fill={PRIMARY_COLOR}>
                {data.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case "composed":
        return (
          <ResponsiveContainer width="100%" height={height - 60}>
            <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey={config.xKey || "date"} tick={{ fontSize: 12 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              {config.bars?.map((bar, idx) => (
                <Bar key={bar} yAxisId="left" dataKey={bar} fill={COLORS[idx % COLORS.length]} name={config.barNames?.[bar] || bar} />
              ))}
              {config.lines?.map((line, idx) => (
                <Line key={line} yAxisId="right" type="monotone" dataKey={line} stroke={COLORS[(idx + 2) % COLORS.length]} name={config.lineNames?.[line] || line} />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        );

      default:
        return <p>Unknown chart type: {chart_type}</p>;
    }
  };

  return (
    <div 
      id="chart-container" 
      data-ready="true"
      style={{ 
        width, 
        height, 
        background: "#fff", 
        padding: "20px",
        fontFamily: "system-ui, -apple-system, sans-serif"
      }}
    >
      {chartData?.title && (
        <h3 style={{ 
          margin: "0 0 10px 0", 
          fontSize: "16px", 
          fontWeight: 600, 
          color: "#1F2937",
          textAlign: "center"
        }}>
          {chartData.title}
        </h3>
      )}
      {renderChart()}
    </div>
  );
};

export default ChartExportPage;
